/**
 * SURSĂ UNICĂ DE ADEVĂR pentru constantele business pe frontend.
 *
 * Toate componentele care au nevoie de:
 *  - URL site (SITE_URL)
 *  - culori LLM-uri / nume LLM-uri
 *  - praguri scor (Lider/Solid/Vulnerabil/Invizibil)
 *
 * trebuie să importe din acest fișier, NU să hardcodeze valori.
 *
 * IMPORTANT: dacă schimbi ponderile sau adaugi/scoți un index aici, asigură-te
 * că `backend/core/business_config.py` (sau echivalent) reflectă aceeași
 * structură. La Faza 3 vom avea un script de validare care verifică sync-ul.
 *
 * 2026-06-09 — i18n refactor: textele lungi de display (label/desc pentru AI rating,
 * label-urile de tier de scor) au cheie i18n sub `app.business.*`. Stringurile-enum
 * logic-coupled (SCORE_TIERS.label, AI_RATING_TIERS.label/desc) rămân în JS ca
 * fallback + canonical pentru comparații. Folosește helperele *I18n / aiRatingText
 * pentru text user-vizibil.
 */

// ── URL site & pre-launch ──────────────────────────────────────
export const SITE_URL = 'https://www.privatebrands.ro';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/logo.jpg`;

// ── Modele LLM (culori folosite în chart-uri, badge-uri, tooltip-uri) ──
export const LLM_MODELS = [
  { id: 'gemini',     name: 'Gemini 2.5 Flash',  short: 'Gemini',     color: '#4285f4' },
  { id: 'chatgpt',    name: 'ChatGPT 5.5',       short: 'ChatGPT',    color: '#10a37f' },
  { id: 'perplexity', name: 'Perplexity Sonar',  short: 'Perplexity', color: '#20808d' },
  { id: 'claude',     name: 'Claude Haiku 4.5',  short: 'Claude',     color: '#c96442' },
];

// Quick-access map: id -> color
export const LLM_COLOR = LLM_MODELS.reduce((m, x) => { m[x.id] = x.color; return m; }, {});
// Quick-access map: short name -> color (pentru locurile care folosesc nume scurt)
export const LLM_COLOR_BY_NAME = LLM_MODELS.reduce((m, x) => { m[x.short] = x.color; return m; }, {});

// Culoare pentru "Scor mediu / agregat"
export const SCORE_AVERAGE_COLOR = '#B8972E';

// ── Praguri scor & label-uri ───────────────────────────────────
// Sortate descrescător (cel mai mare prag primul).
// 2026-05-18 — RECALIBRATE pentru ALIGNMENT cu pragul Cadran (50/50 visibility×authority).
// Anterior 60/45/30 diverga de cadran 50 → un brand putea fi "Solid" tier dar "Visible
// Buried" pe cadran sau invers. Acum aliniate la 50 = pragul "Lider"/"Champion".
//   50+ = Lider AI    (poziție de top — ≥ Champion zone pe cadran)
//   35+ = Solid       (bună prezență dar nu lider)
//   20+ = Vulnerabil  (vizibil ocazional)
//   0+  = Invizibil   (sub radar AI)
export const SCORE_TIERS = [
  { min: 50, label: 'Lider AI',    short: 'Lider',      color: '#2d6a4f', slug: 'leader' },
  { min: 35, label: 'Solid',       short: 'Solid',      color: '#D4AF37', slug: 'solid' },
  { min: 20, label: 'Vulnerabil',  short: 'Vulnerabil', color: '#e67e22', slug: 'vulnerable' },
  { min: 0,  label: 'Invizibil',   short: 'Invizibil',  color: '#c0392b', slug: 'invisible' },
];

// Pragurile pentru tier-uri se citesc din SCORE_TIERS de mai sus (50/35/20/0).
// UI_BAR_HIGH/MID păstrate pentru BACKWARDS COMPAT cu cod care le importă direct,
// dar acum sincronizate cu SCORE_TIERS pentru consistency totală în UI.
export const UI_BAR_HIGH = 50;  // ≥50 verde "Lider AI"
export const UI_BAR_MID  = 35;  // ≥35 gold "Solid"
export const UI_BAR_LOW  = 20;  // ≥20 orange "Vulnerabil", <20 red "Invizibil"

export function scoreTier(score) {
  if (score === null || score === undefined || isNaN(Number(score))) return null;
  const n = Number(score);
  return SCORE_TIERS.find(t => n >= t.min) || SCORE_TIERS[SCORE_TIERS.length - 1];
}

export function scoreLabel(score) {
  return scoreTier(score)?.label || '—';
}

/**
 * Versiune i18n a scoreLabel — întoarce label-ul de tier tradus prin slug.
 * Folosește pentru text user-vizibil; scoreLabel() rămâne pentru compat (RO canonical).
 *
 * @param {number} score
 * @param {Function} t - funcția react-i18next `t`
 * @returns {string} ex: t('app.business.score_tier.leader') sau '—'
 */
export function scoreLabelI18n(score, t) {
  const tr = scoreTier(score);
  return tr ? t('app.business.score_tier.' + tr.slug) : '—';
}

/**
 * Culoare reprezentativa pentru un scor — aliniata cu SCORE_TIERS (single source of truth).
 * Folosita in chart-uri, bar-uri, badge-uri pentru CONSISTENT visual feedback peste tot UI.
 *
 * Threshold-uri: 80 Lider (verde), 60 Solid (gold), 40 Vulnerabil (orange), <40 Invizibil (rosu).
 * NU folosi praguri hardcoded (>=65, >=75, >=85) in alte locuri — foloseste aceasta functie.
 */
export function scoreBarColor(score) {
  const tier = scoreTier(score);
  return tier?.color || 'var(--muted)';
}

// ── AI Rating (overlay Moody's / S&P style) ─────────────────────
// Scorul brut 0-100 e onest tehnic, dar liderii AI cad matematic în zona 55-65 (motoarele împart
// awareness-ul cu 3-4 competitori, 100/100 e structural inatingibil). Asta produce percepție falsă
// la board ("liderul are 57?"). Soluția stil rating agencies: păstrăm scorul brut + adăugăm un
// rating literal calibrat pe realitatea pieței, cu AAA rezervat (niciun brand RO încă) ca
// să lăsăm headroom motivațional.
//
// Praguri calibrate Mai 2026 — pot fi recalibrate când distribuția pieței se schimbă.
export const AI_RATING_TIERS = [
  { min: 75, letter: 'AAA', slug: 'aaa', label: 'AI Native Champion',  color: '#1e6b3a', desc: 'Domină AI search în industrie. Rezervat — niciun brand RO încă.' },
  { min: 60, letter: 'AA',  slug: 'aa',  label: 'AI Native Leader',    color: '#2d6a4f', desc: 'Top-of-mind consistent, halucinări minime, schema.org solid.' },
  { min: 50, letter: 'A',   slug: 'a',   label: 'Strong AI Presence',  color: '#52796f', desc: 'Lider sau top-3 în AI search, dar cu zone de optimizare.' },
  { min: 40, letter: 'BBB', slug: 'bbb', label: 'Established',         color: '#D4AF37', desc: 'Vizibil în AI, dar nu prima alegere. Acuratețe date moderată.' },
  { min: 30, letter: 'BB',  slug: 'bb',  label: 'Emerging',            color: '#B8972E', desc: 'Mențiuni inconsistente, AI te confundă sau te omite des.' },
  { min: 20, letter: 'B',   slug: 'b',   label: 'Developing',          color: '#e67e22', desc: 'AI search te ignoră majoritar; risc reputațional în creștere.' },
  { min: 0,  letter: 'C',   slug: 'c',   label: 'Invisible',           color: '#c0392b', desc: 'Practic invizibil în AI search. Necesită strategie completă.' },
];

export function aiRating(score) {
  if (score === null || score === undefined || isNaN(Number(score))) return null;
  const n = Number(score);
  return AI_RATING_TIERS.find(t => n >= t.min) || AI_RATING_TIERS[AI_RATING_TIERS.length - 1];
}

/**
 * Text i18n pentru un tier de AI rating (label | desc), tradus prin slug.
 * AI_RATING_TIERS.label/desc rămân în JS ca fallback (allowlisted în i18n).
 *
 * @param {object|null} tier - obiectul întors de aiRating(score)
 * @param {('label'|'desc')} field
 * @param {Function} t - funcția react-i18next `t`
 * @returns {string} textul tradus sau '' dacă tier-ul lipsește
 */
export function aiRatingText(tier, field, t) {
  return tier ? t('app.business.ai_rating.' + tier.slug + '.' + field) : '';
}
